// GeneralTable.jsx
import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import "./GeneralTable.scss";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  Search,
  X,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  Inbox,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Built-in helpers — keep these outside the component so they aren't
// recreated on every render and so they're easy to unit test in isolation.
// ---------------------------------------------------------------------------

const STATUS_COLOR_MAP = {
  active: "success",
  approved: "success",
  completed: "success",
  inactive: "neutral",
  pending: "warning",
  "in progress": "warning",
  rejected: "danger",
  declined: "danger",
  terminated: "danger",
};

const getStatusVariant = (rawValue, statusColors) => {
  const key = String(rawValue ?? "").toLowerCase();
  if (statusColors && statusColors[key]) return statusColors[key];
  return STATUS_COLOR_MAP[key] || "neutral";
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

// Resolves the raw value for a column, supporting plain accessors as well
// as a custom `accessorFn` for derived / nested values.
const getRawValue = (row, column) => {
  if (typeof column.accessorFn === "function") return column.accessorFn(row);
  return row[column.accessor];
};

// Resolves the value used for sorting — falls back to the raw cell value,
// but lets columns (e.g. avatar/status cells) declare a dedicated sort key.
const getSortValue = (row, column) => {
  if (typeof column.sortAccessor === "function")
    return column.sortAccessor(row);
  const raw = getRawValue(row, column);
  if (raw && typeof raw === "object") {
    return raw.name ?? raw.label ?? raw.value ?? "";
  }
  return raw;
};

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  if (typeof a === "number" && typeof b === "number") return a - b;

  const aDate = Date.parse(a);
  const bDate = Date.parse(b);
  if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && typeof a === "string") {
    return aDate - bDate;
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

// Renders the content of a single cell based on the column "type". Falls
// back to the historical `row[accessor] || "—"` behaviour for plain columns.
const renderCellContent = (row, column, rowIndex) => {
  if (typeof column.render === "function") {
    return column.render(row, rowIndex);
  }

  const value = getRawValue(row, column);

  switch (column.type) {
    case "status": {
      if (value == null || value === "")
        return <span className="cell-dash">—</span>;
      const variant = getStatusVariant(value, column.statusColors);
      return (
        <span className={`status-chip status-chip--${variant}`}>
          {String(value)}
        </span>
      );
    }

    case "avatar": {
      const person = value || {};
      const name = person.name ?? "—";
      const designation = person.designation ?? person.role ?? "";
      const initials = person.initials ?? getInitials(name);
      return (
        <div className="employee-cell">
          {person.avatarUrl ? (
            <img
              className="employee-avatar employee-avatar--img"
              src={person.avatarUrl}
              alt={name}
            />
          ) : (
            <div className="employee-avatar">{initials || "—"}</div>
          )}
          <div className="employee-info">
            <span className="employee-name" title={name}>
              {name}
            </span>
            {designation && (
              <span className="employee-designation" title={designation}>
                {designation}
              </span>
            )}
          </div>
        </div>
      );
    }

    case "actions": {
      const actions = column.actions || [];
      return (
        <div className="row-actions">
          {actions.map((action, idx) => {
            if (typeof action.hidden === "function" && action.hidden(row))
              return null;
            return (
              <button
                key={action.key || idx}
                type="button"
                className={`row-action-btn ${action.variant ? `row-action-btn--${action.variant}` : ""}`}
                title={action.label}
                aria-label={action.label}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick?.(row, e);
                }}
              >
                <Icon icon={action.icon} className="row-action-icon" />
              </button>
            );
          })}
        </div>
      );
    }

    default: {
      const display = value === 0 ? 0 : value || "—";
      return (
        <span
          className="cell-text"
          title={typeof display === "string" ? display : undefined}
        >
          {display}
        </span>
      );
    }
  }
};

// ---------------------------------------------------------------------------

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const ALL_PAGE_SIZE = "all";

const GeneralTable = ({
  columns,
  data,
  title = "Data Table",
  titleActions = null,
  selectAll = null,
  onSelectAll = null,
  initialPageSize = 50,
  showPagination = true,
  // --- new, optional, backward-compatible props -----------------------
  density = "compact", // 'compact' | 'comfortable' | 'spacious'
  stickyFirstColumn = true,
  stickyLastColumn = true,
  loading = false,
  loadingRows = 8,
  onRowClick = null,
  isRowSelected = null, // (row) => boolean
  rowKey = null, // (row, index) => string | number
  emptyStateTitle = "No data available",
  emptyStateSubtitle = "There's nothing to show here yet.",
  emptyStateAction = null, // { label, onClick }
  defaultSort = null, // { key, direction }
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  resetKey,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [requestedPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState(defaultSort);
  const [pageJumpValue, setPageJumpValue] = useState("");
  const searchInputRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [resetKey]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;
    const lowercasedSearchTerm = searchTerm.toLowerCase();

    return data.filter((row) => {
      if (row.searchableText) {
        return row.searchableText.toLowerCase().includes(lowercasedSearchTerm);
      }

      return Object.values(row).some((value) => {
        if (typeof value === "string" || typeof value === "number") {
          return String(value).toLowerCase().includes(lowercasedSearchTerm);
        }
        return false;
      });
    });
  }, [data, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sort || !sort.key) return filteredData;
    const column = columns.find((c) => (c.accessor || c.key) === sort.key);
    if (!column) return filteredData;

    const sorted = [...filteredData].sort((rowA, rowB) => {
      const result = compareValues(
        getSortValue(rowA, column),
        getSortValue(rowB, column),
      );
      return sort.direction === "desc" ? -result : result;
    });
    return sorted;
  }, [filteredData, sort, columns]);

  // Pagination
  const effectivePageSize =
    pageSize === ALL_PAGE_SIZE ? Math.max(sortedData.length, 1) : pageSize;
  const totalPages = Math.max(
    1,
    Math.ceil(sortedData.length / effectivePageSize),
  );
  // If the data shrinks (filter, refetch) while the user is on a late page,
  // fall back to the last page that still exists instead of showing nothing.
  const currentPage = Math.min(requestedPage, totalPages);
  const startIndex = (currentPage - 1) * effectivePageSize;
  const endIndex = startIndex + effectivePageSize;
  const currentData = showPagination
    ? sortedData.slice(startIndex, endIndex)
    : sortedData;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape" && searchTerm) {
      clearSearch();
    }
  };

  const handlePageSizeChange = (e) => {
    const { value } = e.target;
    setPageSize(value === ALL_PAGE_SIZE ? ALL_PAGE_SIZE : Number(value));
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    searchInputRef.current?.focus();
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(Math.max(1, currentPage - 1));
  const goToNextPage = () =>
    setCurrentPage(Math.min(totalPages, currentPage + 1));

  const handlePageJump = (e) => {
    e.preventDefault();
    const page = Number(pageJumpValue);
    if (Number.isFinite(page) && page >= 1 && page <= totalPages) {
      goToPage(page);
    }
    setPageJumpValue("");
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) buttons.push(i);
    return buttons;
  };

  const handleSort = useCallback((column) => {
    const key = column.accessor || column.key;
    if (column.sortable === false || !key) return;

    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null; // third click clears sorting
    });
    setCurrentPage(1);
  }, []);

  const visibleColumns = columns.filter((c) => !c.hidden);

  return (
    <div className={`general-table-wrapper density-${density}`}>
      {/* Header */}
      <div className="table-heading-column">
        <div className="table-header">
          <div className="table-title-wrapper">
            <h2 className="table-title">{title}</h2>
            {titleActions && (
              <div className="table-title-actions">{titleActions}</div>
            )}

            {selectAll !== null && onSelectAll && (
              <div className="select-all-section">
                {React.cloneElement(selectAll, { onChange: onSelectAll })}
              </div>
            )}
          </div>

          <div className="table-header-controls">
            <div className="table-controls">
              <div className="search-input-wrapper">
                <Search className="search-icon" size={15} aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="search-input"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  aria-label="Search table"
                />
                {searchTerm && (
                  <button
                    className="clear-search-btn"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    type="button"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>

            {showPagination && (
              <div className="page-size-selector">
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="page-size-select"
                  aria-label="Rows per page"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                  <option value={ALL_PAGE_SIZE}>All</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {searchTerm && (
          <div className="search-bar-container">
            <div className="search-results-info">
              {sortedData.length === 0 ? (
                <span className="no-results">
                  No results found for "{searchTerm}"
                </span>
              ) : (
                <span className="results-count">
                  Found {sortedData.length} result
                  {sortedData.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="general-table" role="table" aria-label={title}>
          <thead className="table-head">
            <tr>
              {visibleColumns.map((column, index) => {
                const key = column.accessor || column.key;
                const isSorted = sort?.key === key;
                const sortable = column.sortable !== false && !!key;
                const align = column.align || "left";
                const SortIcon = !isSorted
                  ? ChevronsUpDown
                  : sort.direction === "asc"
                    ? ChevronUp
                    : ChevronDown;

                return (
                  <th
                    key={(column.header || "") + index}
                    className={`table-header-cell align-${align} ${
                      index === 0 ? "first-column" : ""
                    } ${index === 0 && stickyFirstColumn ? "sticky-column" : ""} ${
                      index === visibleColumns.length - 1 && stickyLastColumn
                        ? "sticky-column-right"
                        : ""
                    } ${sortable ? "sortable" : ""}`}
                    style={{
                      width: column.width,
                      minWidth:
                        column.minWidth ?? (index === 0 ? "160px" : "110px"),
                      maxWidth: column.maxWidth,
                    }}
                    title={column.tooltip}
                    aria-sort={
                      isSorted
                        ? sort.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    onClick={() => sortable && handleSort(column)}
                    tabIndex={sortable ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (sortable && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        handleSort(column);
                      }
                    }}
                  >
                    <div className="header-content">
                      <span className="header-text">{column.header}</span>
                      {sortable && (
                        <SortIcon
                          size={14}
                          aria-hidden="true"
                          className={`sort-icon ${isSorted ? "sort-icon--active" : ""}`}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="table-body">
            {loading ? (
              Array.from({ length: loadingRows }).map((_, rowIdx) => (
                <tr
                  key={`skeleton-${rowIdx}`}
                  className="table-row table-row--skeleton"
                >
                  {visibleColumns.map((column, colIdx) => (
                    <td
                      key={`skeleton-${rowIdx}-${colIdx}`}
                      className={`table-cell ${colIdx === 0 ? "first-column" : ""} ${
                        colIdx === 0 && stickyFirstColumn ? "sticky-column" : ""
                      } ${
                        colIdx === visibleColumns.length - 1 && stickyLastColumn
                          ? "sticky-column-right"
                          : ""
                      }`}
                    >
                      <span className="skeleton-bar" />
                    </td>
                  ))}
                </tr>
              ))
            ) : currentData.length > 0 ? (
              currentData.map((row, rowIndex) => {
                const key = rowKey
                  ? rowKey(row, startIndex + rowIndex)
                  : startIndex + rowIndex;
                const selected = isRowSelected ? isRowSelected(row) : false;

                return (
                  <tr
                    key={key}
                    className={`table-row ${onRowClick ? "table-row--clickable" : ""} ${
                      selected ? "table-row--selected" : ""
                    }`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (
                        onRowClick &&
                        e.target === e.currentTarget &&
                        (e.key === "Enter" || e.key === " ")
                      ) {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }}
                  >
                    {visibleColumns.map((column, colIndex) => (
                      <td
                        key={`${key}-${column.accessor || column.key}`}
                        className={`table-cell align-${column.align || "left"} ${
                          colIndex === 0 ? "first-column" : ""
                        } ${colIndex === 0 && stickyFirstColumn ? "sticky-column" : ""} ${
                          colIndex === visibleColumns.length - 1 &&
                          stickyLastColumn
                            ? "sticky-column-right"
                            : ""
                        }`}
                        style={{
                          width: column.width,
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                        }}
                        onClick={
                          column.interactive
                            ? (event) => event.stopPropagation()
                            : undefined
                        }
                      >
                        {renderCellContent(row, column, startIndex + rowIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={visibleColumns.length}>
                  <div className="empty-state">
                    <span className="empty-icon" aria-hidden="true">
                      <Inbox size={22} />
                    </span>
                    <p className="empty-text">{emptyStateTitle}</p>
                    <p className="empty-subtext">{emptyStateSubtitle}</p>
                    {emptyStateAction && (
                      <button
                        type="button"
                        className="empty-state-action"
                        onClick={emptyStateAction.onClick}
                      >
                        {emptyStateAction.label}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && sortedData.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span className="pagination-text">
              Showing {startIndex + 1}–{Math.min(endIndex, sortedData.length)}{" "}
              of {sortedData.length}{" "}
              {sortedData.length === 1 ? "record" : "records"}
            </span>
          </div>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                aria-label="First page"
                type="button"
              >
                <ChevronsLeft
                  size={15}
                  className="pagination-icon"
                  aria-hidden="true"
                />
              </button>

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                aria-label="Previous page"
                type="button"
              >
                <ChevronLeft
                  size={15}
                  className="pagination-icon"
                  aria-hidden="true"
                />
              </button>

              {getPaginationButtons().map((page) => (
                <button
                  key={page}
                  className={`pagination-btn pagination-btn-number ${
                    page === currentPage ? "active" : ""
                  }`}
                  onClick={() => goToPage(page)}
                  aria-label={`Go to page ${page}`}
                  aria-current={page === currentPage ? "page" : undefined}
                  type="button"
                >
                  {page}
                </button>
              ))}

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                type="button"
              >
                <ChevronRight
                  size={15}
                  className="pagination-icon"
                  aria-hidden="true"
                />
              </button>

              <button
                className="pagination-btn pagination-btn-nav"
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                aria-label="Last page"
                type="button"
              >
                <ChevronsRight
                  size={15}
                  className="pagination-icon"
                  aria-hidden="true"
                />
              </button>

              <form className="pagination-jump" onSubmit={handlePageJump}>
                <span className="pagination-jump-label">Go to</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  className="pagination-jump-input"
                  value={pageJumpValue}
                  onChange={(e) => setPageJumpValue(e.target.value)}
                  aria-label="Jump to page"
                  placeholder={String(currentPage)}
                />
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GeneralTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      accessor: PropTypes.string,
      key: PropTypes.string,
      hidden: PropTypes.bool,
      align: PropTypes.oneOf(["left", "center", "right"]),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sortable: PropTypes.bool,
      interactive: PropTypes.bool,
      tooltip: PropTypes.string,
      type: PropTypes.oneOf(["status", "avatar", "actions"]),
      render: PropTypes.func,
      accessorFn: PropTypes.func,
      sortAccessor: PropTypes.func,
      statusColors: PropTypes.object,
      actions: PropTypes.array,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string,
  titleActions: PropTypes.node,
  selectAll: PropTypes.element,
  onSelectAll: PropTypes.func,
  initialPageSize: PropTypes.number,
  showPagination: PropTypes.bool,
  density: PropTypes.oneOf(["compact", "comfortable", "spacious"]),
  stickyFirstColumn: PropTypes.bool,
  stickyLastColumn: PropTypes.bool,
  loading: PropTypes.bool,
  loadingRows: PropTypes.number,
  onRowClick: PropTypes.func,
  isRowSelected: PropTypes.func,
  rowKey: PropTypes.func,
  emptyStateTitle: PropTypes.string,
  emptyStateSubtitle: PropTypes.string,
  emptyStateAction: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  defaultSort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(["asc", "desc"]),
  }),
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  resetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default GeneralTable;
